import { computed, ref, toRaw, watch } from "vue";
import { caseViewService } from "@/services/caseViewService";
import { useAuthStore } from "@/stores/authStore";
const inFlight = new WeakSet();
/** DataTable's is-unread prop for every list whose rows carry the server's flag. */
export const isRowUnread = (row) => row.isUnread;
/** Per-user read state of a case (LAM-5404): opening marks it read, the row menu toggles it in place. */
export function useCaseReadState() {
    const authStore = useAuthStore();
    const canTrack = computed(() => !authStore.userInfo?.isProvider);
    const error = ref("");
    /** Fire and forget: a failed mark leaves the row bold, which is safe, so the page is never interrupted. */
    function markOpened(target) {
        if (!canTrack.value)
            return;
        caseViewService.markSeen(target).catch(() => undefined);
    }
    /** One request per row at a time, so two quick toggles can never land on the server out of order. */
    async function toggle(row, target) {
        const key = toRaw(row);
        if (inFlight.has(key))
            return;
        inFlight.add(key);
        const unread = !row.isUnread;
        row.isUnread = unread;
        error.value = "";
        try {
            await (unread ? caseViewService.markUnread(target) : caseViewService.markSeen(target));
        }
        catch {
            row.isUnread = !unread;
            error.value = unread ? "Couldn't mark as unread. Try again." : "Couldn't mark as read. Try again.";
        }
        finally {
            inFlight.delete(key);
        }
    }
    return { canTrack, markOpened, toggle, error };
}
/** Mark the page's case read each time the page opens one, by the record key in its route. */
export function useMarkCaseOpened(kind, id) {
    const { markOpened } = useCaseReadState();
    watch(id, (value) => {
        if (value === undefined || value === null || value === "")
            return;
        const target = kind === "intakeOrderId" ? { intakeOrderId: Number(value) } : { [kind]: String(value) };
        markOpened(target);
    }, { immediate: true });
}
